import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useDuplicateTrResume } from 'builder/hooks/useDuplicateTrResume'

import * as SimpleForm from 'builder/components/SimpleForm'
import SelectResumeModal from 'builder/views/ResumeDistributionView/components/SelectResumeModal'

import { DocumentTypes, FetchStatuses } from 'builder/modules/constants'
import { actions } from 'builder/modules/autoApply/autoApplyModule'
import { BriefDocumentType, selectors, actions as documentActions } from 'builder/modules/panel'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { useUser } from 'builder/hooks/useUser'
import { useSelectResume } from '../../hooks/useSelectResume'
import { useStepsConfig } from '../../hooks/useStepsConfig'
import { StartCompleteScreenRoute } from '../../constants'

import { FillWarning, Title, TitleWrapper, FormContainer } from '../styles'
import StackOfPapersImgSrc from '../../assets/stack_of_papers.png'
import * as Styles from './styles'
import { TRANSLATION } from './constants'
import { ResumeUpload } from './ResumeUpload/ResumeUpload'
import { ResumeSelection } from './ResumeSelection/ResumeSelection'
import { QuestionnaireInfo } from './QuestionnaireInfo/QuestionnaireInfo'
import { TRResumeSelection } from './TRResumeSelection/TRResumeSelection'
import { Item } from './Item/Item'

export const ResumeSelectView = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const stepsConfig = useStepsConfig()

  const user = useUser()

  const trDocuments = useTypedSelector(store =>
    selectors.documentsByType(store, DocumentTypes.trDocument),
  )

  const lastCreatedTrResume = useMemo(() => {
    if (!trDocuments) {
      return null
    }

    return trDocuments
      .filter(doc => doc.doc_type === 'resume')
      .sort((a, b) => Date.parse(b.created_at.toString()) - Date.parse(a.created_at.toString()))[0]
  }, [trDocuments])

  const documentsStatus = useTypedSelector(selectors.documentsStatus)

  const onNextStep = () => {
    navigate(`/auto-apply/${stepsConfig[StartCompleteScreenRoute.RESUME_SELECT].next}`)
  }

  const onResumeSuccess = (data: AutoApplyForm) => {
    dispatch(actions.updateForm(data))
    onNextStep()
  }

  const {
    isError: hasDuplicationError,
    mutateAsync: duplicateResume,
    isLoading: isDuplicationLoading,
  } = useDuplicateTrResume()
  const { mutateAsync: selectResume, isLoading, isError } = useSelectResume(onResumeSuccess)

  const [showResumeSelect, setShowResumeSelect] = useState(false)

  const onBuilderResumeSelect = async (resumeDetails: BriefDocumentType) => {
    const isTRDocument = resumeDetails.type === DocumentTypes.trDocument
    const payload = isTRDocument ? { trResumeId: resumeDetails.id } : { resumeId: resumeDetails.id }

    if (isTRDocument && !resumeDetails.resume_id) {
      await duplicateResume({ id: resumeDetails.id })
    }

    selectResume(payload)
    trackInternalEvent('submit_resume', {
      type: isTRDocument ? 'TR' : 'app',
      label: 'total_job_search_solution',
    })
  }

  useEffect(() => {
    trackInternalEvent('aa_resume_selection', {
      label: 'total_job_search_solution',
    })
    dispatch(documentActions.fetchDocuments())
  }, [dispatch])

  const renderBody = () => {
    if (lastCreatedTrResume) {
      return (
        <>
          <TRResumeSelection
            isLoading={documentsStatus === FetchStatuses.loading}
            isDulpicationLoading={isDuplicationLoading || isLoading}
            resumeTitle={lastCreatedTrResume?.title}
            onClick={() => lastCreatedTrResume && onBuilderResumeSelect(lastCreatedTrResume)}
            whiteLabelBrand={user?.scopes?.kind}
          />
          <Styles.ColumnContainer>
            <ResumeUpload onResumeSuccess={onResumeSuccess} IsTopResumeOrIsTopCV />

            <Item
              showBorder
              onClick={() => setShowResumeSelect(true)}
              icon={<img src={StackOfPapersImgSrc} />}
              title={i18n.t(`${TRANSLATION}.change_resume_button.title`)}
              subtitle={i18n.t(`${TRANSLATION}.change_resume_button.subtitle`)}
              style={{ marginTop: '24px' }}
            />
          </Styles.ColumnContainer>
        </>
      )
    }

    return (
      <>
        <ResumeSelection
          isLoading={documentsStatus === FetchStatuses.loading}
          hasDuplicationError={hasDuplicationError || isError}
          isDulpicationLoading={isDuplicationLoading || isLoading}
          setShowResumeSelect={setShowResumeSelect}
        />
        <ResumeUpload onResumeSuccess={onResumeSuccess} />
      </>
    )
  }

  return (
    <Styles.Wrapper>
      <Styles.Container>
        <TitleWrapper>
          <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
          <FillWarning>
            {i18n.t(`${TRANSLATION}.sub_title1`)} <br /> {i18n.t(`${TRANSLATION}.sub_title2`)}
          </FillWarning>
        </TitleWrapper>
        <FormContainer>
          <SimpleForm.Form>
            <Styles.BoxWrapper>{renderBody()}</Styles.BoxWrapper>
          </SimpleForm.Form>
        </FormContainer>
      </Styles.Container>
      <QuestionnaireInfo />
      {showResumeSelect && (
        <SelectResumeModal
          title={i18n.t(`${TRANSLATION}.modal_title`)}
          onClose={() => setShowResumeSelect(false)}
          submitSelectedResume={onBuilderResumeSelect}
          isTotalJobSearchFlow
          isDocumentLoaded
        />
      )}
    </Styles.Wrapper>
  )
}
